<script lang="ts">
  /**
   * Typewriter component — renders text character by character.
   *
   * Props:
   *   text      — the full string to reveal
   *   wpm       — words per minute (100-600, default 200)
   *   onComplete — fires once when the full text has been shown
   */

  import { untrack } from "svelte";

  interface Props {
    text: string;
    wpm?: number;
    onComplete?: () => void;
  }

  let { text, wpm = 200, onComplete }: Props = $props();

  /** Number of characters currently visible. */
  let charIndex = $state(0);

  /** Whether playback is still in progress. */
  let isPlaying = $derived(charIndex < text.length);

  /** Space-hold acceleration multiplier (2x to 10x). */
  let speedMultiplier = $state(1);
  let spaceHeld = $state(false);
  let spaceHeldStart = $state(0);

  /** Reference to the scroll container. */
  let scrollEl: HTMLDivElement | undefined = $state();

  /** Base delay in ms per character: 60 000 / (wpm * 5). */
  const baseDelay = $derived(60000 / (wpm * 5));

  /* ---- timer ---- */
  let timerId: ReturnType<typeof setTimeout> | null = null;

  function scheduleNext(): void {
    if (charIndex >= text.length) return;
    const delay = baseDelay / speedMultiplier;
    timerId = setTimeout(tick, delay);
  }

  function tick(): void {
    if (charIndex >= text.length) return;
    charIndex++;
    if (charIndex >= text.length) {
      onComplete?.();
    } else {
      scheduleNext();
    }
  }

  function clearTimer(): void {
    if (timerId !== null) {
      clearTimeout(timerId);
      timerId = null;
    }
  }

  /** Skip to end instantly. */
  function skipToEnd(): void {
    clearTimer();
    charIndex = text.length;
    onComplete?.();
  }

  /* ---- reactivity: restart when text changes ---- */
  $effect(() => {
    // Subscribe to `text` so this re-runs when the prop changes.
    void text;
    charIndex = 0;
    speedMultiplier = 1;
    spaceHeld = false;
    clearTimer();
    // untrack prevents scheduleNext's read of charIndex from becoming
    // a dependency — otherwise every tick would re-trigger this effect.
    untrack(() => scheduleNext());

    return () => clearTimer();
  });

  /* ---- auto-scroll ---- */
  $effect(() => {
    void charIndex;
    if (scrollEl) {
      scrollEl.scrollTop = scrollEl.scrollHeight;
    }
  });

  /* ---- space-hold ramp timer ---- */
  let rampId: ReturnType<typeof setInterval> | null = null;

  function startSpaceRamp(): void {
    if (spaceHeld) return;
    spaceHeld = true;
    spaceHeldStart = Date.now();
    speedMultiplier = 2;
    rampId = setInterval(() => {
      const elapsed = (Date.now() - spaceHeldStart) / 1000;
      speedMultiplier = Math.min(2 + Math.floor(elapsed), 10);
    }, 1000);
    // Restart timer with new speed
    clearTimer();
    scheduleNext();
  }

  function stopSpaceRamp(): void {
    spaceHeld = false;
    speedMultiplier = 1;
    if (rampId !== null) {
      clearInterval(rampId);
      rampId = null;
    }
    // Restart timer at normal speed
    clearTimer();
    if (isPlaying) scheduleNext();
  }

  /* ---- keyboard handling ---- */
  function handleKeydown(e: KeyboardEvent): void {
    if (!isPlaying) return;
    if (e.key === "Enter") {
      e.preventDefault();
      skipToEnd();
    } else if (e.key === " ") {
      e.preventDefault();
      startSpaceRamp();
    }
  }

  function handleKeyup(e: KeyboardEvent): void {
    if (e.key === " ") {
      stopSpaceRamp();
    }
  }
</script>

<svelte:window onkeydown={handleKeydown} onkeyup={handleKeyup} />

<div class="typewriter__wrap">
  <div class="typewriter" bind:this={scrollEl}>
    <span class="typewriter__text">{text.slice(0, charIndex)}</span>{#if isPlaying}<span
        class="typewriter__cursor">_</span
      >{/if}
  </div>

  {#if isPlaying}
    <button class="typewriter__skip" onclick={skipToEnd}> Skip >> </button>
  {/if}
</div>

<style>
  .typewriter__wrap {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .typewriter {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
    padding-bottom: 3rem;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: var(--font-mono);
    font-size: 1rem;
    line-height: 1.5;
    color: var(--fg);
  }

  .typewriter__text {
    color: var(--fg);
  }

  .typewriter__cursor {
    animation: blink 0.6s step-start infinite;
    color: var(--fg-bright);
    font-weight: bold;
  }

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }

  .typewriter__skip {
    position: absolute;
    bottom: 0.5rem;
    right: 1rem;
    background: transparent;
    border: 1px solid var(--fg-dim);
    color: var(--fg-dim);
    font-family: var(--font-mono);
    font-size: 0.75rem;
    padding: 0.3rem 0.75rem;
    min-height: 44px;
    cursor: pointer;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    z-index: 2;
  }

  .typewriter__skip:active {
    background: rgba(51, 255, 51, 0.15);
    color: var(--fg-bright);
    border-color: var(--fg-bright);
  }
</style>
